import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Settings, Upload, Save } from 'lucide-react';
import axiosInstance from '../../lib/axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const SiteSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    site_name: 'SiteControl',
    site_title: '',
    site_logo: '',
    site_favicon: '',
    site_description: '',
    login_background_url: 'https://images.unsplash.com/photo-1710746055183-ba55e616bfe8?crop=entropy&cs=srgb&fm=jpg&q=85',
    overdue_days: 7,
    default_priority: 'medium',
    enable_notifications: true,
    auto_assign_tickets: false,
    date_format: 'MM/DD/YYYY',
    timezone: 'UTC',
    tickets_per_page: 10
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [faviconFile, setFaviconFile] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState('');
  const [loginBackgroundPreview, setLoginBackgroundPreview] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings');
      setSettings(response.data);
      if (response.data.site_logo) {
        // Set the full URL for preview
        setLogoPreview(`${window.location.origin}${response.data.site_logo}`);
      } else {
        setLogoPreview('');
      }
      if (response.data.site_favicon) {
        setFaviconPreview(`${window.location.origin}${response.data.site_favicon}`);
      } else {
        setFaviconPreview('');
      }
      if (response.data.login_background_url) {
        // Check if it's a URL or a path
        if (response.data.login_background_url.startsWith('http')) {
          setLoginBackgroundPreview(response.data.login_background_url);
        } else {
          setLoginBackgroundPreview(`${window.location.origin}${response.data.login_background_url}`);
        }
      } else {
        setLoginBackgroundPreview('');
      }
      setLogoFile(null); // Clear the file input state after fetch
      setFaviconFile(null);
    } catch (error) {
      console.error('Failed to fetch settings');
      // Use defaults if settings don't exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      
      // Add all settings
      Object.keys(settings).forEach(key => {
        if (key !== 'site_logo' && key !== 'site_favicon') {
          formData.append(key, settings[key]);
        }
      });
      
      // Add logo file if changed
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      
      // Add favicon file if changed
      if (faviconFile) {
        formData.append('favicon', faviconFile);
      }

      await axiosInstance.put('/settings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t('messages.success.saved'));
      fetchSettings();
    } catch (error) {
      toast.error(t('messages.error.saveFailed'));
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCleanupDeletedUsers = async () => {
    if (!window.confirm('Clean up deleted user references from projects and tickets? This will update the user counts.')) {
      return;
    }
    
    try {
      const response = await axiosInstance.post('/admin/cleanup-deleted-users');
      toast.success(`Cleanup completed! Projects cleaned: ${response.data.projects_cleaned}, Tickets cleaned: ${response.data.tickets_cleaned}`);
    } catch (error) {
      console.error('Failed to cleanup deleted users:', error);
      toast.error('Failed to cleanup deleted users');
    }
  };

  const handleCleanupNotifications = async () => {
    if (!window.confirm('Clear all user notifications? This will delete all notifications for all users.')) {
      return;
    }
    
    try {
      const response = await axiosInstance.post('/admin/cleanup-notifications');
      toast.success(`Notifications cleared! Deleted: ${response.data.notifications_deleted}`);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      toast.error('Failed to clear notifications');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight flex items-center gap-3">
            <Settings className="w-10 h-10" />
            Site Settings
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage global system configuration and preferences
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Branding */}
        <Card>
          <CardHeader>
            <CardTitle>Branding</CardTitle>
            <CardDescription>Customize your site&apos;s appearance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="site_name">Site Name</Label>
              <Input
                id="site_name"
                value={settings.site_name}
                onChange={(e) => handleChange('site_name', e.target.value)}
                placeholder="Enter site name"
              />
              <p className="text-xs text-muted-foreground">
                This name will appear in the header and navigation
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="site_title">Browser Tab Title</Label>
              <Input
                id="site_title"
                value={settings.site_title}
                onChange={(e) => handleChange('site_title', e.target.value)}
                placeholder="Enter browser tab title"
                maxLength={60}
              />
              <p className="text-xs text-muted-foreground">
                This title will appear in browser tabs (max 60 characters). If empty, Site Name will be used.
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="logo">Site Logo</Label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <div className="w-24 h-24 border-2 border-dashed border-border rounded-lg flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                    <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain p-2" />
                  </div>
                )}
                <div className="flex-1">
                  <Input
                    id="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: Square image, max 500x500px. Light logos will be more visible with contrasting backgrounds.
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="favicon">Favicon Icon</Label>
              <div className="flex items-center gap-4">
                {faviconPreview && (
                  <div className="w-16 h-16 border-2 border-dashed border-border rounded-lg flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                    <img src={faviconPreview} alt="Favicon preview" className="max-w-full max-h-full object-contain p-1" />
                  </div>
                )}
                <div className="flex-1">
                  <Input
                    id="favicon"
                    type="file"
                    accept="image/x-icon,image/png,image/svg+xml"
                    onChange={handleFaviconChange}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Recommended: 32x32px or 64x64px. Supports .ico, .png, .svg formats.
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="site_description">Website Description</Label>
              <Input
                id="site_description"
                value={settings.site_description}
                onChange={(e) => handleChange('site_description', e.target.value)}
                placeholder="Enter website description"
                maxLength={160}
              />
              <p className="text-xs text-muted-foreground">
                Brief description of your site (max 160 characters). Used for SEO and browser tabs.
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="login_background_url">Login Page Background Image URL</Label>
              <div className="space-y-4">
                {loginBackgroundPreview && (
                  <div className="border rounded-lg overflow-hidden">
                    <img 
                      src={loginBackgroundPreview} 
                      alt="Login background preview" 
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div>
                  <Input
                    id="login_background_url"
                    type="url"
                    value={settings.login_background_url || ''}
                    onChange={(e) => {
                      handleChange('login_background_url', e.target.value);
                      setLoginBackgroundPreview(e.target.value);
                    }}
                    placeholder="https://example.com/background-image.jpg"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter the direct URL of a background image. Recommended: High-quality landscape image, 1920x1080px or larger.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ticket Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Ticket Settings</CardTitle>
            <CardDescription>Configure ticket behavior and defaults</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="overdue_days">Overdue After (days)</Label>
                <Input
                  id="overdue_days"
                  type="number"
                  min="1"
                  max="365"
                  value={settings.overdue_days}
                  onChange={(e) => handleChange('overdue_days', parseInt(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Tickets will be marked as overdue after this many days
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default_priority">Default Priority</Label>
                <Select
                  value={settings.default_priority}
                  onValueChange={(value) => handleChange('default_priority', value)}
                >
                  <SelectTrigger id="default_priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Default priority for new tickets
                </p>
              </div>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Auto-assign Tickets</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically assign tickets to project team members
                </p>
              </div>
              <Switch
                checked={settings.auto_assign_tickets}
                onCheckedChange={(checked) => handleChange('auto_assign_tickets', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Display Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Display & Format</CardTitle>
            <CardDescription>Customize how data is displayed</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="date_format">Date Format</Label>
                <Select
                  value={settings.date_format}
                  onValueChange={(value) => handleChange('date_format', value)}
                >
                  <SelectTrigger id="date_format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (US)</SelectItem>
                    <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (EU)</SelectItem>
                    <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (ISO)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={settings.timezone}
                  onValueChange={(value) => handleChange('timezone', value)}
                >
                  <SelectTrigger id="timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                    <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                    <SelectItem value="Asia/Taipei">Taipei Time (TST)</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokyo Time (JST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tickets_per_page">Tickets Per Page</Label>
                <Select
                  value={String(settings.tickets_per_page)}
                  onValueChange={(value) => handleChange('tickets_per_page', parseInt(value))}
                >
                  <SelectTrigger id="tickets_per_page">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Default number of tickets shown per page
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
            <CardDescription>Control system notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Notifications</Label>
                <p className="text-xs text-muted-foreground">
                  Send notifications for ticket assignments and updates
                </p>
              </div>
              <Switch
                checked={settings.enable_notifications}
                onCheckedChange={(checked) => handleChange('enable_notifications', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Section */}
        <Card>
          <CardHeader>
            <CardTitle>系統維護 / System Maintenance</CardTitle>
            <CardDescription>Database cleanup and maintenance tools</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>清理已刪除用戶 / Cleanup Deleted Users</Label>
              <p className="text-sm text-muted-foreground mb-2">
                If project user counts are incorrect after deleting users, click this button to clean up old references. This will update all projects and tickets to remove deleted user IDs.
              </p>
              <Button 
                onClick={handleCleanupDeletedUsers}
                variant="outline"
                className="w-full"
              >
                Run User Cleanup
              </Button>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>清理通知 / Clear All Notifications</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Clear all user notifications from the system. This will delete all notifications for all users. Useful for removing old test notifications from production.
              </p>
              <Button 
                onClick={handleCleanupNotifications}
                variant="outline"
                className="w-full"
              >
                Clear All Notifications
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SiteSettings;
